/**
 * Put a car on the Daily Photo List's Reshoots sheet (Ian, 2026-11-03): one
 * sheet for every store's cars that need to be shot again. Design and
 * rulings: .claude/plans/photo-list-reshoots.md.
 *
 * INVI is not shooting yet, so the person has seen the bad photo elsewhere
 * and arrives with a stock number or a VIN. Either one looks the car up —
 * today's list first, then the latest inventory audit — and fills the rest:
 * VIN, stock, the car's own store sheet, year/make/model/trim, color and lot.
 * A car nobody knows falls back to the VIN decode for year/make/model, and
 * the person picks the sheet. VIN, stock and sheet are required (the same
 * rules as a hand-added car); the reason is optional and is posted as a note.
 *
 * Modal chrome, the lookup flow and the styles copy
 * components/photos/AddCarToListModal.tsx — there is no shared Dialog
 * component yet in INVI.
 */
import React, { useEffect, useRef, useState } from "react"

import { decodeVin } from "../../api/photoCapture"
import { lookupReshootCar } from "../../api/photoList"
import type { ReshootLookupResponse, ReshootRequest } from "../../api/photoList"
import { THEME } from "../../config/theme"
import { PHOTO_LIST_STORE_SHEETS, type PhotoListStoreSheetSlug } from "../../context/PhotoListContext"
import { useIsMobile } from "../../hooks/useIsMobile"
import { tint } from "../../utils/color"

const VIN_LENGTH = 17
// No I, O or Q — they never appear in a VIN (same rule as the server).
const VIN_CHARACTERS = /^[A-HJ-NPR-Z0-9]+$/
// Mirror ReshootRequest's limits in backend/schemas.py, so the server never
// answers with a validation error the person can't read.
const MIN_YEAR = 1900
const MAX_YEAR = 2100
const YEAR_PATTERN = /^\d{4}$/
const STOCK_MAX_LENGTH = 32
const TEXT_MAX_LENGTH = 64
const REASON_MAX_LENGTH = 500

/** The values the last lookup typed in, so the next lookup can clear exactly
 * those and never something the person typed themselves. */
interface LookupFill {
  vin: string
  stock: string
  sheet: string
  year: string
  make: string
  model: string
  trim: string
  color: string
  lot: string
}

const EMPTY_FILL: LookupFill = {
  vin: "", stock: "", sheet: "", year: "", make: "", model: "", trim: "", color: "", lot: "",
}

/** A cleared field stays cleared only if it still holds what the previous
 * lookup put there; anything else is the person's own typing. */
function clearIfFromLookup(current: string, filled: string): string {
  return filled !== "" && current === filled ? "" : current
}

/** Keep the person's own typing; fill only a blank field. */
function fillIfBlank(current: string, found: string): string {
  return current.trim() === "" ? found : current
}

function sourceSentence(found: ReshootLookupResponse): string {
  const sheetLabel = PHOTO_LIST_STORE_SHEETS.find((s) => s.slug === found.sheet)?.label
  return sheetLabel
    ? `Found in inventory (${sheetLabel}).`
    : "Found in inventory. Pick the car's sheet."
}

interface Props {
  /** The active dealer group — the lookup reads that group's list and
   * inventory, and an operator's account is pinned to none. */
  dealerGroupId: number | null
  onClose: () => void
  /** Saves the reshoot; rejects with the sentence to show (e.g. "That car is
   * already on the Reshoots sheet."). The modal closes on success. */
  onSubmit: (request: ReshootRequest) => Promise<unknown>
}

export default function RequestReshootModal({ dealerGroupId, onClose, onSubmit }: Props) {
  const isMobile = useIsMobile()
  const [vin, setVin] = useState("")
  const [stock, setStock] = useState("")
  const [sheet, setSheet] = useState<PhotoListStoreSheetSlug | "">("")
  const [year, setYear] = useState("")
  const [make, setMake] = useState("")
  const [model, setModel] = useState("")
  const [trim, setTrim] = useState("")
  const [color, setColor] = useState("")
  const [lot, setLot] = useState("")
  const [reason, setReason] = useState("")

  const [lookingUp, setLookingUp] = useState(false)
  const [lookupNotice, setLookupNotice] = useState<string | null>(null)
  // The store sheet the looked-up car is on today, when it is: such a car
  // needs photos, not a reshoot, and the server would refuse it.
  const [onTodaysList, setOnTodaysList] = useState<string | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  // Look a VIN up only when it changes to a NEW complete one, so editing back
  // and forth doesn't call the server on every keystroke.
  const lastLookedUpVinRef = useRef<string | null>(null)
  const lastFillRef = useRef<LookupFill>(EMPTY_FILL)
  // The lookup in flight; a newer one cancels an older one's answer.
  const lookupTokenRef = useRef(0)

  const normalizedVin = vin.trim().toUpperCase()
  const vinIsComplete =
    normalizedVin.length === VIN_LENGTH && VIN_CHARACTERS.test(normalizedVin)
  const canSubmit =
    vinIsComplete && stock.trim() !== "" && sheet !== "" && onTodaysList === null && !submitting

  const clearPreviousFill = () => {
    const previous = lastFillRef.current
    lastFillRef.current = EMPTY_FILL
    setVin((current) => clearIfFromLookup(current, previous.vin))
    setStock((current) => clearIfFromLookup(current, previous.stock))
    setSheet((current) => clearIfFromLookup(current, previous.sheet) as PhotoListStoreSheetSlug | "")
    setYear((current) => clearIfFromLookup(current, previous.year))
    setMake((current) => clearIfFromLookup(current, previous.make))
    setModel((current) => clearIfFromLookup(current, previous.model))
    setTrim((current) => clearIfFromLookup(current, previous.trim))
    setColor((current) => clearIfFromLookup(current, previous.color))
    setLot((current) => clearIfFromLookup(current, previous.lot))
  }

  const applyFound = (found: ReshootLookupResponse) => {
    const knownSheet = PHOTO_LIST_STORE_SHEETS.some((s) => s.slug === found.sheet)
    const fill: LookupFill = {
      vin: found.vin ?? "",
      stock: found.stock ?? "",
      sheet: knownSheet && found.sheet ? found.sheet : "",
      year: found.year !== null ? String(found.year) : "",
      make: found.make ?? "",
      model: found.model ?? "",
      trim: found.trim ?? "",
      color: found.color_ext ?? "",
      lot: found.stargard ?? "",
    }
    lastFillRef.current = fill
    // A VIN the lookup typed in is already looked up: the VIN effect must
    // not run it again and overwrite this answer.
    if (fill.vin) lastLookedUpVinRef.current = fill.vin.trim().toUpperCase()
    if (fill.vin) setVin((current) => fillIfBlank(current, fill.vin))
    if (fill.stock) setStock((current) => fillIfBlank(current, fill.stock))
    if (fill.sheet) setSheet((current) => (current === "" ? (fill.sheet as PhotoListStoreSheetSlug) : current))
    if (fill.year) setYear((current) => fillIfBlank(current, fill.year))
    if (fill.make) setMake((current) => fillIfBlank(current, fill.make))
    if (fill.model) setModel((current) => fillIfBlank(current, fill.model))
    if (fill.trim) setTrim((current) => fillIfBlank(current, fill.trim))
    if (fill.color) setColor((current) => fillIfBlank(current, fill.color))
    if (fill.lot) setLot((current) => fillIfBlank(current, fill.lot))
    setLookupNotice(sourceSentence(found))
  }

  const runLookup = async (query: { vin?: string; stock?: string }) => {
    const token = ++lookupTokenRef.current
    clearPreviousFill()
    setLookingUp(true)
    setLookupNotice(null)
    setOnTodaysList(null)
    try {
      const found = await lookupReshootCar(query, dealerGroupId)
      if (token !== lookupTokenRef.current) return
      if (found.found) {
        applyFound(found)
        setOnTodaysList(found.on_todays_list)
        return
      }
      if (query.vin) {
        // Nobody knows the car: at least year/make/model from the VIN itself.
        const decoded = await decodeVin(query.vin)
        if (token !== lookupTokenRef.current) return
        const fill: LookupFill = {
          ...EMPTY_FILL,
          year: decoded.year !== null ? String(decoded.year) : "",
          make: decoded.make ?? "",
          model: decoded.model ?? "",
        }
        lastFillRef.current = fill
        if (fill.year) setYear((current) => fillIfBlank(current, fill.year))
        if (fill.make) setMake((current) => fillIfBlank(current, fill.make))
        if (fill.model) setModel((current) => fillIfBlank(current, fill.model))
        setLookupNotice(
          "Not on today's list or in inventory. Filled what the VIN says; pick the car's sheet and type the rest.",
        )
      } else {
        setLookupNotice("No car with that stock number on today's list or in inventory. Type the VIN.")
      }
    } catch {
      if (token === lookupTokenRef.current) {
        setLookupNotice("The lookup didn't answer. Type the details below.")
      }
    } finally {
      if (token === lookupTokenRef.current) setLookingUp(false)
    }
  }

  // A complete VIN looks itself up.
  useEffect(() => {
    if (!vinIsComplete) {
      lastLookedUpVinRef.current = null
      return
    }
    if (lastLookedUpVinRef.current === normalizedVin) return
    lastLookedUpVinRef.current = normalizedVin
    void runLookup({ vin: normalizedVin })
    // runLookup reads refs, setters and the group; the VIN is the one input.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalizedVin, vinIsComplete])

  const lookUpByStock = () => {
    const typed = stock.trim()
    if (typed === "" || lookingUp) return
    // The stock the last lookup itself filled in is already answered —
    // tabbing out of the field must not look the same car up again.
    if (typed.toUpperCase() === lastFillRef.current.stock.trim().toUpperCase()) return
    void runLookup({ stock: typed })
  }

  // Escape closes, like the notes modal.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
    }
    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [onClose])

  const handleSubmit = async () => {
    setSubmitError(null)
    if (!vinIsComplete) {
      setSubmitError("A VIN is 17 letters and numbers, without I, O or Q.")
      return
    }
    if (stock.trim() === "") {
      setSubmitError("Enter the car's stock number.")
      return
    }
    if (sheet === "") {
      setSubmitError("Pick the car's own store sheet.")
      return
    }
    const trimmedYear = year.trim()
    const parsedYear = trimmedYear === "" ? null : Number(trimmedYear)
    if (
      parsedYear !== null &&
      (!YEAR_PATTERN.test(trimmedYear) || parsedYear < MIN_YEAR || parsedYear > MAX_YEAR)
    ) {
      setSubmitError("Year must be 4 digits, like 2024.")
      return
    }
    setSubmitting(true)
    try {
      await onSubmit({
        vin: normalizedVin,
        stock: stock.trim(),
        sheet,
        year: parsedYear,
        make: make.trim() || null,
        model: model.trim() || null,
        trim: trim.trim() || null,
        color_ext: color.trim() || null,
        stargard: lot.trim() || null,
        reason: reason.trim() || null,
      })
      onClose()
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : "Could not add the reshoot.")
      setSubmitting(false)
    }
  }

  const fieldRow: React.CSSProperties = {
    ...rowFieldsStyle,
    ...(isMobile && { flexDirection: "column", gap: 0 }),
  }

  return (
    <div style={backdropStyle} onClick={onClose}>
      <div
        role="dialog"
        aria-label="Add a car to the Reshoots sheet"
        style={{
          ...dialogStyle,
          ...(isMobile && {
            minWidth: 0,
            width: "calc(100vw - 24px)",
            padding: THEME.spacing.md,
          }),
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={headerStyle}>
          <h2 style={titleStyle}>Add a reshoot</h2>
          <button type="button" onClick={onClose} style={closeButtonStyle} aria-label="Close">
            ×
          </button>
        </div>

        <p style={hintStyle}>
          The car goes to the top of the Reshoots sheet and stays there every
          morning until its Done circle is tapped. Type the stock number or
          the VIN and INVI fills in the rest.
        </p>

        <div style={fieldRow}>
          <label style={{ ...fieldStyle, flex: 1 }}>
            <span style={fieldLabelStyle}>Stock #</span>
            <div style={{ display: "flex", gap: THEME.spacing.xs }}>
              <input
                type="text"
                value={stock}
                onChange={(e) => setStock(e.target.value)}
                onBlur={lookUpByStock}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault()
                    lookUpByStock()
                  }
                }}
                style={{ ...inputStyle, flex: 1, minWidth: 0 }}
                maxLength={STOCK_MAX_LENGTH}
                autoFocus
              />
              <button
                type="button"
                onClick={lookUpByStock}
                disabled={stock.trim() === "" || lookingUp}
                style={lookupButtonStyle(stock.trim() !== "" && !lookingUp)}
              >
                Look up
              </button>
            </div>
          </label>
        </div>

        <label style={fieldStyle}>
          <span style={fieldLabelStyle}>
            VIN {lookingUp && <span style={inlineStatusStyle}> — looking up…</span>}
          </span>
          <input
            type="text"
            value={vin}
            // Spaces dropped as typed or pasted: a VIN has none.
            onChange={(e) => setVin(e.target.value.replace(/\s+/g, ""))}
            placeholder="17-character VIN"
            style={{ ...inputStyle, fontFamily: THEME.fonts.mono, textTransform: "uppercase" }}
            autoCapitalize="characters"
            autoCorrect="off"
            spellCheck={false}
          />
          {normalizedVin.length > 0 && (
            <span style={fieldHintStyle}>
              {normalizedVin.length}/{VIN_LENGTH} characters
            </span>
          )}
        </label>

        {lookupNotice && <div style={noticeStyle}>{lookupNotice}</div>}
        {onTodaysList && (
          <div style={errorStyle}>
            That car is on {onTodaysList} today. It needs photos, not a reshoot.
          </div>
        )}

        <label style={fieldStyle}>
          <span style={fieldLabelStyle}>Car&apos;s sheet</span>
          <select
            value={sheet}
            onChange={(e) => setSheet(e.target.value as PhotoListStoreSheetSlug | "")}
            style={inputStyle}
          >
            <option value="">— pick the car&apos;s store sheet —</option>
            {PHOTO_LIST_STORE_SHEETS.map((option) => (
              <option key={option.slug} value={option.slug}>
                {option.label}
              </option>
            ))}
          </select>
        </label>

        <div style={fieldRow}>
          <label style={{ ...fieldStyle, ...(isMobile ? {} : { flex: "0 0 90px" }) }}>
            <span style={fieldLabelStyle}>Year</span>
            <input
              type="text"
              inputMode="numeric"
              value={year}
              onChange={(e) => setYear(e.target.value)}
              style={inputStyle}
              maxLength={4}
            />
          </label>
          <label style={{ ...fieldStyle, flex: 1 }}>
            <span style={fieldLabelStyle}>Make</span>
            <input type="text" value={make} onChange={(e) => setMake(e.target.value)} style={inputStyle} maxLength={TEXT_MAX_LENGTH} />
          </label>
          <label style={{ ...fieldStyle, flex: 1 }}>
            <span style={fieldLabelStyle}>Model</span>
            <input type="text" value={model} onChange={(e) => setModel(e.target.value)} style={inputStyle} maxLength={TEXT_MAX_LENGTH} />
          </label>
        </div>

        <div style={fieldRow}>
          <label style={{ ...fieldStyle, flex: 1 }}>
            <span style={fieldLabelStyle}>Trim (optional)</span>
            <input type="text" value={trim} onChange={(e) => setTrim(e.target.value)} style={inputStyle} maxLength={TEXT_MAX_LENGTH} />
          </label>
          <label style={{ ...fieldStyle, flex: 1 }}>
            <span style={fieldLabelStyle}>Color (optional)</span>
            <input type="text" value={color} onChange={(e) => setColor(e.target.value)} style={inputStyle} maxLength={TEXT_MAX_LENGTH} />
          </label>
          <label style={{ ...fieldStyle, flex: 1 }}>
            <span style={fieldLabelStyle}>Lot (optional)</span>
            <input type="text" value={lot} onChange={(e) => setLot(e.target.value)} style={inputStyle} maxLength={TEXT_MAX_LENGTH} />
          </label>
        </div>

        <label style={fieldStyle}>
          <span style={fieldLabelStyle}>What needs redoing (optional)</span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="driver side is blurry, redo the exterior"
            style={{ ...inputStyle, minHeight: 56, resize: "vertical" }}
            maxLength={REASON_MAX_LENGTH}
          />
        </label>

        {submitError && <div style={errorStyle}>{submitError}</div>}

        <div style={footerStyle}>
          <button type="button" onClick={onClose} style={secondaryButtonStyle}>
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            style={primaryButtonStyle(canSubmit)}
          >
            {submitting ? "Adding…" : "Add to Reshoots"}
          </button>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Styles — copied from components/photos/AddCarToListModal.tsx (~line 300).
// ---------------------------------------------------------------------------

const backdropStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: THEME.modal.overlay,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000,
}

const dialogStyle: React.CSSProperties = {
  background: THEME.colors.neutral.light.surface,
  borderRadius: THEME.borderRadius.lg,
  padding: THEME.spacing.lg,
  minWidth: 520,
  maxWidth: 620,
  maxHeight: "90vh",
  overflowY: "auto",
  fontFamily: THEME.fonts.primary,
  boxShadow: THEME.modal.shadow,
}

const headerStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: THEME.spacing.sm,
}

const titleStyle: React.CSSProperties = {
  fontSize: 18,
  fontWeight: 600,
  color: THEME.colors.neutral.light.textPrimary,
  margin: 0,
}

const closeButtonStyle: React.CSSProperties = {
  background: "transparent",
  border: "none",
  fontSize: 24,
  cursor: "pointer",
  color: THEME.colors.neutral.light.textSecondary,
  lineHeight: 1,
}

const hintStyle: React.CSSProperties = {
  fontSize: 13,
  color: THEME.colors.neutral.light.textSecondary,
  margin: 0,
  marginBottom: THEME.spacing.md,
}

const fieldStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: THEME.spacing.xs,
  marginBottom: THEME.spacing.md,
}

const rowFieldsStyle: React.CSSProperties = {
  display: "flex",
  gap: THEME.spacing.sm,
}

const fieldLabelStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 500,
  letterSpacing: "0.5px",
  textTransform: "uppercase",
  color: THEME.colors.neutral.light.textSecondary,
}

const inlineStatusStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 400,
  textTransform: "none",
  letterSpacing: 0,
  color: THEME.colors.neutral.light.textSecondary,
}

const fieldHintStyle: React.CSSProperties = {
  fontSize: 11,
  color: THEME.colors.neutral.light.textSecondary,
}

const inputStyle: React.CSSProperties = {
  fontSize: 13,
  padding: `${THEME.spacing.xs} ${THEME.spacing.sm}`,
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.md,
  background: THEME.colors.neutral.light.surface,
  color: THEME.colors.neutral.light.textPrimary,
  fontFamily: THEME.fonts.primary,
}

const noticeStyle: React.CSSProperties = {
  fontSize: 12,
  background: tint(THEME.auditTable.tabColors.pending, 0.5),
  border: `1px solid ${THEME.auditTable.tabColors.pending}`,
  borderRadius: THEME.borderRadius.md,
  padding: `${THEME.spacing.xs} ${THEME.spacing.sm}`,
  color: THEME.colors.neutral.light.textPrimary,
  marginBottom: THEME.spacing.md,
}

const errorStyle: React.CSSProperties = {
  fontSize: 12,
  background: tint(THEME.colors.semantic.danger, 0.1),
  border: `1px solid ${THEME.colors.semantic.danger}`,
  borderRadius: THEME.borderRadius.md,
  padding: `${THEME.spacing.xs} ${THEME.spacing.sm}`,
  color: THEME.colors.semantic.danger,
  marginBottom: THEME.spacing.md,
}

const footerStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "flex-end",
  gap: THEME.spacing.sm,
}

const primaryButtonStyle = (enabled: boolean): React.CSSProperties => ({
  fontSize: 13,
  fontWeight: 500,
  padding: `${THEME.spacing.xs} ${THEME.spacing.md}`,
  borderRadius: THEME.borderRadius.md,
  border: "none",
  background: enabled ? THEME.colors.navy : THEME.colors.silver,
  color: THEME.colors.white,
  cursor: enabled ? "pointer" : "not-allowed",
  fontFamily: THEME.fonts.primary,
})

// The secondary button's tokens, sized to sit beside the stock box.
const lookupButtonStyle = (enabled: boolean): React.CSSProperties => ({
  fontSize: 13,
  padding: `${THEME.spacing.xs} ${THEME.spacing.md}`,
  borderRadius: THEME.borderRadius.md,
  border: `1px solid ${THEME.colors.silver}`,
  background: THEME.colors.neutral.light.surface,
  color: enabled ? THEME.colors.neutral.light.textPrimary : THEME.colors.neutral.light.textSecondary,
  cursor: enabled ? "pointer" : "not-allowed",
  fontFamily: THEME.fonts.primary,
  whiteSpace: "nowrap",
})

const secondaryButtonStyle: React.CSSProperties = {
  fontSize: 13,
  padding: `${THEME.spacing.xs} ${THEME.spacing.md}`,
  borderRadius: THEME.borderRadius.md,
  border: `1px solid ${THEME.colors.silver}`,
  background: THEME.colors.neutral.light.surface,
  color: THEME.colors.neutral.light.textPrimary,
  cursor: "pointer",
  fontFamily: THEME.fonts.primary,
}
